import { Navbar } from "@/components/Navbar";
import { Clock, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

export default function PendingApproval() {
  const { user, logout } = useAuth();
  if (!user) return null;
  const rejected = user.status === "rejected";
  const suspended = user.status === "suspended";
  const noApp = user.status === "pending" && !user.application_submitted_at;

  let title = "Application under review";
  let intro = "Thanks for submitting. Our team is reviewing your application. You'll receive an email as soon as it's approved.";
  let accent = "bg-[#FF9500] text-black";
  let badge = "UNDER REVIEW";
  if (noApp) {
    title = "Complete your application";
    intro = "Welcome! Before we can approve your shop, please complete the application form. It takes about 15 minutes and you can save and come back anytime.";
    accent = "bg-[#007AFF] text-white";
    badge = "ACTION REQUIRED";
  }
  if (rejected) {
    title = "Application not approved";
    intro = "We're unable to approve your shop application at this time.";
    accent = "bg-[#FF3B30] text-white";
    badge = "REJECTED";
  } else if (suspended) {
    title = "Account suspended";
    intro = "Your shop account is currently suspended. While suspended, you cannot send quotes or messages.";
    accent = "bg-[#FF3B30] text-white";
    badge = "SUSPENDED";
  }

  return (
    <div className="min-h-screen bg-[#09090B] safe-top safe-bottom">
      <Navbar />
      <main className="max-w-[700px] mx-auto px-4 sm:px-8 lg:px-12 py-16" data-testid="pending-approval-page">
        <div className="border border-white/10 bg-[#121214] p-8 sm:p-12">
          <div className="flex items-center gap-3 mb-6">
            <span className={`text-xs uppercase tracking-widest px-2 py-1 ${accent}`} data-testid="status-badge">{badge}</span>
            <span className="label-eyebrow text-zinc-500">Shop account</span>
          </div>

          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-[#FF3B30] flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">{title}</h1>
              <p className="text-zinc-400 leading-relaxed">{intro}</p>
            </div>
          </div>

          {(user.rejection_reason || user.suspension_reason) && (
            <div className="border border-white/10 bg-[#09090B] p-4 mb-6">
              <div className="label-eyebrow text-zinc-500 mb-2">REASON</div>
              <p className="text-sm text-zinc-300">{user.rejection_reason || user.suspension_reason}</p>
            </div>
          )}

          <div className="border-t border-white/10 pt-6 mt-8">
            <div className="label-eyebrow mb-4">{noApp ? "WHAT WE NEED FROM YOU" : "WHILE YOU WAIT"}</div>
            {noApp ? (
              <ul className="space-y-3 text-sm text-zinc-400">
                <li className="flex gap-3"><span className="text-[#FF3B30]">→</span>Business identity & tax info</li>
                <li className="flex gap-3"><span className="text-[#FF3B30]">→</span>Address, service radius, and shop photos</li>
                <li className="flex gap-3"><span className="text-[#FF3B30]">→</span>Service categories & warranty offered</li>
                <li className="flex gap-3"><span className="text-[#FF3B30]">→</span>State business license + insurance documents</li>
                <li className="flex gap-3"><span className="text-[#FF3B30]">→</span>5 quick agreements</li>
              </ul>
            ) : (
              <ul className="space-y-3 text-sm text-zinc-400">
                <li className="flex gap-3"><span className="text-[#FF3B30]">→</span>Edit your application if you need to update anything.</li>
                <li className="flex gap-3"><span className="text-[#FF3B30]">→</span>You'll get an email the moment we approve you.</li>
              </ul>
            )}
            <div className="flex flex-wrap gap-3 mt-6">
              {noApp ? (
                <Link to="/shop/apply" className="btn-primary" data-testid="pending-start-app-btn">Start application</Link>
              ) : !rejected && !suspended ? (
                <Link to="/shop/apply" className="btn-secondary" data-testid="pending-edit-app-btn">Edit application</Link>
              ) : null}
              <Link to="/settings" className="btn-secondary" data-testid="pending-settings-link">Account settings</Link>
              <button onClick={logout} className="btn-secondary" data-testid="pending-logout-btn">Sign out</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
